// ═══════════════════════════════════════════════════════════════════════════════
// Activity Types
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  ActivityAddress,
  ActivityField,
  ActivityReason,
  ActivityRow,
  ActivityStageInfo,
  ActivityTeam,
  ActivityWorkflowInfo,
  IconValue,
  IssueTypeChip,
  NoteMedia,
  NoteReaction,
  NoteRow,
  UserChip,
} from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

export const GROUP_THRESHOLD = 3;
export const MAX_DISPLAY_COUNT = 3;
export const DEFAULT_ISSUE_NAME = "Project";
// Changeset subjects the server writes for whole-issue lifecycle events.
// Each renders as "<verb> this <issue name>".
export const LIFECYCLE_ACTIONS: readonly string[] = [
  "archived",
  "unarchived",
  "trashed",
  "restored",
];

/**
 * The bare lifecycle verb a changeset subject names, or null for any other
 * subject. Stored changesets carry two spellings — `issue.archived` on rows
 * written by the pre-NestJS core, bare `archived` on everything since — and no
 * migration rewrites the older keys, so both reach the feed.
 */
export function lifecycleVerb(subject: string): string | null {
  const verb = subject.startsWith("issue.") ? subject.slice(6) : subject;
  return LIFECYCLE_ACTIONS.includes(verb) ? verb : null;
}
export const SCROLL_THRESHOLD = 100;

// ═══════════════════════════════════════════════════════════════════════════════
// Server data (the timeline's local names for the fragments it selects)
// ═══════════════════════════════════════════════════════════════════════════════
//
// Every name below is a row alias over a fragment in graphql/activities.graphql
// or graphql/notes.graphql, so none can drift from the schema.

export type ActivityAuthor = UserChip;
export type ActivityIcon = IconValue;
export type ActivityIssueTypeInfo = IssueTypeChip;
export type ActivityNode = ActivityRow;
export type {
  ActivityAddress,
  ActivityField,
  ActivityReason,
  ActivityStageInfo,
  ActivityTeam,
  ActivityWorkflowInfo,
  NoteMedia,
  NoteReaction,
};

/**
 * A note plus the replies threaded onto it. Threading is client-side — the
 * server returns replies as their own connection under `replies`, and the
 * activity view nests them — so `replies` is the one part of this shape the
 * `NoteCard` fragment does not describe.
 */
export type NoteNode = NoteRow & { replies?: NoteNode[] };

/** An activity that carries a note: a created note, or an age visit whose
 *  combined changeset references one. */
export function isNoteActivity(activity: ActivityNode) {
  return Boolean(
    (!activity.changeset?.note?.before && activity.changeset?.note?.after) ||
      activity.changeset?.age?.after?.noteId,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Processed types (used by ActivityList for grouping/rendering)
// ═══════════════════════════════════════════════════════════════════════════════

export interface ActivityDayGroup {
  dateKey: string; // local-day key "2026-02-26"; matches DateJumpGroup
  dateLabel: string; // "Today" | "Yesterday" | "February 26"
  timestamp: number;
  segments: ActivitySegment[];
}

export type ActivitySegment =
  | {
      type: "note";
      activity: ActivityNode;
      last: boolean;
    }
  | {
      type: "activities";
      activities: (ActivityNode & { isLast: boolean })[];
      collapsed: boolean;
      /** Stable key used for collapse state — matches the key in collapsedSegments map. */
      segmentKey: string;
    };

/** Processed change object passed to chunk components */
export type ActivityChange = Omittable<{
  action: string;
  target: { type: string; label: string; referenceId?: string };
  subject?: string;
  oldValue?: any;
  newValue?: any;
  users?: ActivityAuthor[];
  teams?: ActivityTeam[];
  fields?: ActivityField[];
  oldStage?: ActivityStageInfo | null;
  newStage?: ActivityStageInfo | null;
  oldWorkflow?: ActivityWorkflowInfo | null;
  newWorkflow?: ActivityWorkflowInfo | null;
  oldType?: ActivityIssueTypeInfo | null;
  newType?: ActivityIssueTypeInfo | null;
  oldAddress?: ActivityAddress | null;
  newAddress?: ActivityAddress | null;
  note?: any;
  noteReply?: any;
}>;
